import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif';
const INP = { width: '100%', padding: '10px 13px', border: 'none', borderRadius: 10, fontSize: 14, outline: 'none', background: '#F2F2F7', color: '#1C1C1E', fontFamily: FONT, boxSizing: 'border-box' };
const LBL = { display: 'block', fontSize: 11, fontWeight: 600, color: '#8E8E93', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6, fontFamily: FONT };
const SEC = { fontSize: 12, fontWeight: 700, color: '#1C1C1E', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10, marginTop: 4, fontFamily: FONT };

const COST_FIELDS = [
  ['shipping',          'Shipping Cost'],
  ['importFees',        'Import Fees'],
  ['customsDuties',     'Customs / Duties'],
  ['brokerFees',        'Broker Fees'],
  ['portFees',          'Port Fees'],
  ['trucking',          'Trucking / Drayage'],
  ['insurance',         'Insurance'],
  ['warehouseReceiving','Warehouse Receiving'],
  ['other',             'Other Costs'],
];

const ALLOC_METHODS = [
  ['value',    'By Product Value (default)'],
  ['quantity', 'By Quantity'],
  ['weight',   'By Weight (kg)'],
  ['volume',   'By Volume (CBM)'],
  ['manual',   'Manual (custom %)'],
];


export default function ShipmentModal({ shipment, onSave, onClose }) {
  const [form, setForm] = useState({
    name: '', supplier: '', origin: 'Vietnam', containerNumber: '',
    arrivalDate: '', allocationMethod: 'value', status: 'draft', notes: '',
    costs: { shipping: '', importFees: '', customsDuties: '', brokerFees: '',
             portFees: '', trucking: '', insurance: '', warehouseReceiving: '', other: '' },
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (shipment) {
      setForm({
        ...shipment,
        costs: {
          shipping: shipment.costs?.shipping ?? '',
          importFees: shipment.costs?.importFees ?? '',
          customsDuties: shipment.costs?.customsDuties ?? '',
          brokerFees: shipment.costs?.brokerFees ?? '',
          portFees: shipment.costs?.portFees ?? '',
          trucking: shipment.costs?.trucking ?? '',
          insurance: shipment.costs?.insurance ?? '',
          warehouseReceiving: shipment.costs?.warehouseReceiving ?? '',
          other: shipment.costs?.other ?? '',
        },
      });
    }
  }, [shipment]);

  function setField(k, v) { setForm(f => ({ ...f, [k]: v })); }
  function setCost(k, v) { setForm(f => ({ ...f, costs: { ...f.costs, [k]: v } })); }

  const totalShared = COST_FIELDS.reduce((s, [k]) => s + (parseFloat(form.costs[k]) || 0), 0);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError('Shipment name is required.'); return; }
    setSaving(true); setError('');
    try {
      const costs = {};
      COST_FIELDS.forEach(([k]) => { costs[k] = parseFloat(form.costs[k]) || 0; });
      await onSave({ ...form, costs });
      onClose();
    } catch (err) { setError(err.message || 'Failed to save.'); }
    finally { setSaving(false); }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: FONT }}>
      <div style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 640, maxHeight: '92vh', overflow: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.28)' }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px', position: 'sticky', top: 0, background: 'white', zIndex: 1, borderBottom: '1px solid #F2F2F7' }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1C1C1E', margin: 0 }}>{shipment ? 'Edit Shipment' : 'New Shipment'}</h2>
          <button onClick={onClose} style={{ background: '#F2F2F7', border: 'none', cursor: 'pointer', borderRadius: '50%', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8E8E93' }}>
            <X size={15} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '20px 24px 24px' }}>
          {error && <div style={{ background: '#FFF1F0', borderRadius: 10, padding: '10px 14px', color: '#FF3B30', fontSize: 13, marginBottom: 14 }}>{error}</div>}

          {/* Basic info */}
          <div style={{ marginBottom: 14 }}>
            <label style={LBL}>Shipment Name *</label>
            <input type="text" value={form.name} onChange={e => setField('name', e.target.value)} required style={INP} placeholder="e.g. Vietnam Container #12 — May 2026" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={LBL}>Supplier</label>
              <input type="text" value={form.supplier} onChange={e => setField('supplier', e.target.value)} style={INP} placeholder="Supplier name" />
            </div>
            <div>
              <label style={LBL}>Country of Origin</label>
              <input type="text" value={form.origin} onChange={e => setField('origin', e.target.value)} style={INP} placeholder="e.g. Vietnam" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={LBL}>Container Number</label>
              <input type="text" value={form.containerNumber} onChange={e => setField('containerNumber', e.target.value)} style={INP} placeholder="e.g. MSCU1234567" />
            </div>
            <div>
              <label style={LBL}>Arrival Date</label>
              <input type="date" value={form.arrivalDate} onChange={e => setField('arrivalDate', e.target.value)} style={INP} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            <div>
              <label style={LBL}>Allocation Method</label>
              <select value={form.allocationMethod} onChange={e => setField('allocationMethod', e.target.value)} style={INP}>
                {ALLOC_METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label style={LBL}>Status</label>
              <select value={form.status} onChange={e => setField('status', e.target.value)} style={INP}>
                <option value="draft">Draft</option>
                <option value="received">Received</option>
                <option value="costed">Costed</option>
              </select>
            </div>
          </div>

          {/* Shared costs */}
          <div style={{ background: '#F9F9FB', borderRadius: 14, padding: '16px 18px', marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <p style={{ ...SEC, margin: 0 }}>Shared Costs (split across all products)</p>
              {totalShared > 0 && (
                <span style={{ fontSize: 13, fontWeight: 700, color: '#84CC16' }}>
                  Total: ${totalShared.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              {COST_FIELDS.map(([key, label]) => (
                <div key={key}>
                  <label style={LBL}>{label}</label>
                  <input type="number" min="0" step="0.01" value={form.costs[key]} onChange={e => setCost(key, e.target.value)} style={INP} placeholder="0.00" />
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 22 }}>
            <label style={LBL}>Notes</label>
            <textarea value={form.notes} onChange={e => setField('notes', e.target.value)} rows={2} style={{ ...INP, resize: 'vertical' }} placeholder="Optional notes…" />
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding: '10px 20px', border: 'none', borderRadius: 12, background: '#F2F2F7', color: '#1C1C1E', cursor: 'pointer', fontSize: 14, fontWeight: 500, fontFamily: FONT }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding: '10px 24px', border: 'none', borderRadius: 12, background: '#84CC16', color: 'white', cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600, opacity: saving ? 0.8 : 1, fontFamily: FONT }}>
              {saving ? 'Saving…' : shipment ? 'Update' : 'Create Shipment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
